import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import './AdminRMACreate.css';

function AdminRMACreate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [selectedItems, setSelectedItems] = useState({});
  const [reason, setReason] = useState('');

  // Gauti užsakymo duomenis
  const { data: order, isLoading } = useQuery({
    queryKey: ['admin-order', id],
    queryFn: async () => {
      const res = await apiClient.get(`/admin/orders/${id}`);
      return res.data;
    },
  });

  // Sukurti RMA
  const createRMAMutation = useMutation({
    mutationFn: async (data) => {
      return apiClient.post('/admin/rma', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-order', id]);
      queryClient.invalidateQueries(['admin-orders']);
      notification.success('Grąžinimo prašymas sukurtas sėkmingai');
      navigate(`/admin/orders/${id}`);
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida kuriant grąžinimo prašymą');
    },
  });

  const handleItemToggle = (itemId, maxQuantity) => {
    setSelectedItems((prev) => {
      const newState = { ...prev };
      if (newState[itemId]) {
        delete newState[itemId];
      } else {
        newState[itemId] = { quantity: 1, maxQuantity };
      }
      return newState;
    });
  };

  const handleQuantityChange = (itemId, quantity, maxQuantity) => {
    const qty = Math.max(1, Math.min(parseInt(quantity) || 1, maxQuantity));
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], quantity: qty },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (Object.keys(selectedItems).length === 0) {
      notification.info('Pasirinkite bent vieną prekę grąžinimui');
      return;
    }

    if (!reason.trim()) {
      notification.warning('Įveskite grąžinimo priežastį');
      return;
    }

    const items = Object.entries(selectedItems).map(([orderItemId, data]) => ({
      order_item_id: parseInt(orderItemId),
      quantity: data.quantity,
    }));

    createRMAMutation.mutate({
      order_id: parseInt(id),
      reason: reason.trim(),
      items,
    });
  };

  if (isLoading) {
    return <div className="admin-rma-create-page container">Kraunama...</div>;
  }

  if (!order) {
    return <div className="admin-rma-create-page container">Užsakymas nerastas</div>;
  }

  return (
    <div className="admin-rma-create-page container">
      <div className="rma-header">
        <h1>Sukurti grąžinimo prašymą (RMA)</h1>
        <p className="order-info">
          Užsakymas: <strong>{order.order_number}</strong>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="rma-form">
        {/* Prekių pasirinkimas */}
        <div className="form-section">
          <h2>Pasirinkite prekes grąžinimui</h2>
          <div className="items-list">
            {order.items && order.items.length > 0 ? (
              order.items.map((item) => {
                const isSelected = !!selectedItems[item.id];
                const selectedData = selectedItems[item.id];
                const maxQuantity = item.quantity;

                return (
                  <div
                    key={item.id}
                    className={`item-card ${isSelected ? 'selected' : ''}`}
                  >
                    <div className="item-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleItemToggle(item.id, maxQuantity)}
                      />
                    </div>
                    <div className="item-info">
                      <h3>{item.product_name}</h3>
                      {item.variant_name && (
                        <p className="variant-name">Variantas: {item.variant_name}</p>
                      )}
                      <p className="item-sku">SKU: {item.product_sku || '—'}</p>
                      <p className="item-price">
                        Kaina: {parseFloat(item.price).toFixed(2)} €
                      </p>
                    </div>
                    <div className="item-quantity">
                      {isSelected ? (
                        <div className="quantity-controls">
                          <label>
                            Kiekis grąžinimui:
                            <input
                              type="number"
                              min="1"
                              max={maxQuantity}
                              value={selectedData.quantity}
                              onChange={(e) =>
                                handleQuantityChange(
                                  item.id,
                                  e.target.value,
                                  maxQuantity
                                )
                              }
                              className="quantity-input"
                            />
                            <span className="max-quantity">
                              / {maxQuantity} (užsakyta)
                            </span>
                          </label>
                          <div className="refund-amount">
                            Grąžinimo suma:{' '}
                            <strong>
                              {(
                                (parseFloat(item.price) * selectedData.quantity)
                              ).toFixed(2)}{' '}
                              €
                            </strong>
                          </div>
                        </div>
                      ) : (
                        <div className="item-total">
                          Užsakyta: {item.quantity} vnt.
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="empty-message">Užsakyme nėra prekių</p>
            )}
          </div>
        </div>

        {/* Priežastis */}
        <div className="form-section">
          <h2>Grąžinimo priežastis</h2>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Aprašykite, kodėl grąžinamos prekės..."
            className="reason-textarea"
            rows="5"
            required
          />
        </div>

        {/* Suma */}
        {Object.keys(selectedItems).length > 0 && (
          <div className="form-section summary-section">
            <h2>Grąžinimo santrauka</h2>
            <div className="summary-box">
              <div className="summary-row">
                <span>Pasirinktų prekių:</span>
                <span>{Object.keys(selectedItems).length}</span>
              </div>
              <div className="summary-row">
                <span>Bendras grąžinimo kiekis:</span>
                <span>
                  {Object.values(selectedItems).reduce(
                    (sum, data) => sum + data.quantity,
                    0
                  )}{' '}
                  vnt.
                </span>
              </div>
              <div className="summary-row total">
                <span>Bendra grąžinimo suma:</span>
                <span>
                  {order.items
                    .filter((item) => selectedItems[item.id])
                    .reduce((sum, item) => {
                      const selectedData = selectedItems[item.id];
                      return (
                        sum +
                        parseFloat(item.price) * selectedData.quantity
                      );
                    }, 0)
                    .toFixed(2)}{' '}
                  €
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Mygtukai */}
        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate(`/admin/orders/${id}`)}
            className="btn btn-secondary"
          >
            Atšaukti
          </button>
          <button
            type="submit"
            disabled={createRMAMutation.isLoading}
            className="btn btn-primary"
          >
            {createRMAMutation.isLoading
              ? 'Kuriama...'
              : 'Sukurti grąžinimo prašymą'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdminRMACreate;
